import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const toArray = (value, keys = []) => {
  if (Array.isArray(value)) return value;

  if (value && typeof value === "object") {
    for (const key of keys) {
      if (Array.isArray(value[key])) return value[key];
    }

    if (value.data && Array.isArray(value.data)) {
      return value.data;
    }
  }

  return [];
};

export default function Shop() {
  const [params, setParams] = useSearchParams();

  const [schools, setSchools] = useState([]);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [schoolsLoading, setSchoolsLoading] = useState(true);

  const [category, setCategory] = useState("All");

  const selectedSchool = params.get("school") || "all";

  // --------------------------------------------------
  // LOAD SCHOOLS
  // --------------------------------------------------

  useEffect(() => {
    let mounted = true;

    const loadSchools = async () => {
      setSchoolsLoading(true);

      try {
        const response = await api.get("/schools");

        if (!mounted) return;

        const schoolList = toArray(response?.data, [
          "schools",
          "items",
          "results",
        ]);

        setSchools(schoolList);
      } catch (error) {
        console.error("Unable to load schools:", error);

        if (mounted) {
          setSchools([]);
        }
      } finally {
        if (mounted) {
          setSchoolsLoading(false);
        }
      }
    };

    loadSchools();

    return () => {
      mounted = false;
    };
  }, []);

  // --------------------------------------------------
  // LOAD PRODUCTS
  // --------------------------------------------------

  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      setLoading(true);

      try {
        const query =
          selectedSchool !== "all"
            ? `?school_id=${encodeURIComponent(selectedSchool)}`
            : "";

        const response = await api.get(`/products${query}`);

        if (!mounted) return;

        const productList = toArray(response?.data, [
          "products",
          "items",
          "results",
        ]);

        setProducts(productList);
      } catch (error) {
        console.error("Unable to load products:", error);

        if (mounted) {
          setProducts([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      mounted = false;
    };
  }, [selectedSchool]);

  // --------------------------------------------------
  // SCHOOL MAP
  // --------------------------------------------------

  const schoolMap = useMemo(() => {
    if (!Array.isArray(schools)) {
      return {};
    }

    return Object.fromEntries(
      schools
        .filter((school) => school && school.id)
        .map((school) => [school.id, school])
    );
  }, [schools]);

  // --------------------------------------------------
  // CATEGORIES
  // --------------------------------------------------

  const categories = useMemo(() => {
    if (!Array.isArray(products)) {
      return ["All"];
    }

    const uniqueCategories = Array.from(
      new Set(
        products
          .map((product) => product?.category)
          .filter(Boolean)
      )
    );

    return ["All", ...uniqueCategories];
  }, [products]);

  // --------------------------------------------------
  // FILTER PRODUCTS
  // --------------------------------------------------

  const filtered = useMemo(() => {
    if (!Array.isArray(products)) {
      return [];
    }

    return products
      .filter((product) => product && product.id)
      .map((product) => ({
        ...product,
        school_name:
          schoolMap[product.school_id]?.short_name ||
          schoolMap[product.school_id]?.name ||
          "",
      }))
      .filter(
        (product) =>
          category === "All" || product.category === category
      );
  }, [products, schoolMap, category]);

  // --------------------------------------------------
  // SCHOOL FILTER
  // --------------------------------------------------

  const setSchool = (id) => {
    if (id === "all") {
      setParams({});
    } else {
      setParams({ school: id });
    }

    setCategory("All");
  };

  const activeSchool =
    selectedSchool !== "all"
      ? schoolMap[selectedSchool]
      : null;

  // --------------------------------------------------
  // PAGE
  // --------------------------------------------------

  return (
    <div className="min-h-screen">

      {/* PAGE HEADER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-28 pb-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4"
        >
          The Catalog
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-6xl tracking-tighter leading-none max-w-3xl"
          data-testid="shop-heading"
        >
          {activeSchool
            ? activeSchool.name
            : "Every garment, every school."}
        </motion.h1>
      </section>

      {/* SCHOOL FILTER */}
      <section className="border-y border-border sticky top-20 z-30 bg-background/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex gap-2 overflow-x-auto py-4">

          <button
            onClick={() => setSchool("all")}
            data-testid="filter-school-all"
            className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition-colors ${
              selectedSchool === "all"
                ? "bg-foreground text-background border-foreground"
                : "border-border hover:border-foreground"
            }`}
          >
            All Schools
          </button>

          {!schoolsLoading &&
            Array.isArray(schools) &&
            schools.map((school) => (
              <button
                key={school.id}
                onClick={() => setSchool(school.id)}
                data-testid={`filter-school-${school.id}`}
                className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition-colors ${
                  selectedSchool === school.id
                    ? "bg-foreground text-background border-foreground"
                    : "border-border hover:border-foreground"
                }`}
              >
                {school.short_name || school.name}
              </button>
            ))}
        </div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-wrap gap-2">

        {Array.isArray(categories) &&
          categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              data-testid={`filter-category-${cat}`}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 transition-colors ${
                category === cat
                  ? "text-accent border-b border-accent"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {cat}
            </button>
          ))}

      </section>

      {/* PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-28">

        {loading ? (
          <p
            className="text-muted-foreground py-20 text-center"
            data-testid="shop-loading"
          >
            Loading catalog…
          </p>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center">
            <p
              className="text-muted-foreground"
              data-testid="shop-empty"
            >
              No products found.
            </p>

            {selectedSchool !== "all" && (
              <button
                onClick={() => setSchool("all")}
                className="mt-4 text-sm underline underline-offset-4 hover:text-accent"
              >
                View all schools
              </button>
            )}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
            data-testid="product-grid"
          >
            {filtered.map((product, index) => (
              <ProductCard
                key={product.id}
                product={product}
                index={index}
              />
            ))}
          </div>
        )}

      </section>

    </div>
  );
}
